import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, type Circonscription, type Echelon } from './api';
import { NATIONALITES } from '../../src/partage/nationalites';
import { decoderNin, normaliserNin, validerNin } from '../../src/partage/nin';
import { reduireEnJpeg } from './image-reduite';
import { aplatir, lieuDuNin, resoudreSaisie } from '../../src/partage/lieux';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LES CHAMPS DU GUICHET — écrits UNE fois pour tous les formulaires d'État.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Demandes du fondateur, 24/09/2026 : préfecture et commune choisies dans une
 * liste avec recherche et en cascade, nationalité en liste avec « Guinéenne »
 * en premier, NIN obligatoire et vérifié, saisie en majuscules.
 *
 * ⚠️ ÉCRITS ICI ET PAS DANS `guichet.tsx`. Ce formulaire porte dix types
 * d'acte, deux parties, deux témoins, un défunt et jusqu'à trente héritiers :
 * la même adresse y est demandée sept fois. Sept copies divergent au premier
 * correctif appliqué à une seule — c'est le motif que `CadrePartie` a déjà
 * fermé pour l'état civil, et `identiteComparable` avant lui.
 */

/* ═══ MAJUSCULES ═══════════════════════════════════════════════════════════
 * ⚠️ SUR LA VALEUR, JAMAIS EN CSS. `text-transform: uppercase` afficherait
 * « TOURÉ » et enverrait « toure » : la base, l'acte imprimé et la fiche des
 * parties recevraient la minuscule, et personne ne verrait l'écart avant le
 * papier. Mesuré sur GN-DEM-000000033 le 24/09/2026 : « matoto », « boke »,
 * « conakry », « N'faly » — un formulaire déposé entièrement en minuscules.
 *
 * ⚠️ `toLocaleUpperCase('fr')` GARDE LES ACCENTS : TOURÉ, pas TOURE. L'état
 * civil guinéen les porte, et les perdre casserait le rapprochement autant
 * qu'une faute de frappe.
 *
 * ⚠️ LE COURRIEL EST LA SEULE EXCEPTION, et elle se dit à l'écran. La partie
 * locale d'une adresse est sensible à la casse : la mettre en capitales peut
 * rendre le récépissé indélivrable.
 */
export const majuscule = (v: string): string => v.toLocaleUpperCase('fr-FR');

/**
 * Un champ texte dont la valeur est mise en capitales à la frappe.
 *
 * ⚠️ LE CURSEUR EST REPOSÉ. Réécrire `value` sans restaurer `selectionStart`
 * renvoie le curseur en fin de champ à chaque touche : corriger une lettre au
 * milieu d'un nom devient impossible. La longueur ne change pas, la position
 * est donc conservable telle quelle.
 */
export function SaisieMajuscule(
  { valeur, onValeur, ...reste }:
  { valeur: string; onValeur: (v: string) => void }
  & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>,
) {
  return (
    <input
      {...reste}
      value={valeur}
      onChange={(e) => {
        const i = e.target.selectionStart;
        const v = majuscule(e.target.value);
        onValeur(v);
        if (i !== null) window.requestAnimationFrame(() => {
          try { e.target.setSelectionRange(i, i); } catch { /* champ démonté */ }
        });
      }}
    />
  );
}

/* ═══ LISTE FERMÉE AVEC RECHERCHE ══════════════════════════════════════════ */

export interface Entree { valeur: string; libelle: string; meta?: string; echelon?: string }

/**
 * ⚠️ LISTE FERMÉE, ET LA SAISIE LIBRE EST EFFACÉE À LA SORTIE. Garder un texte
 * qui ne correspond à rien laisserait croire à un choix fait ; c'est exactement
 * ce que les champs libres « Préfecture » et « Commune » produisaient — une
 * affaire partie à 250 km de son fonds le 23/09/2026, et « matoto » en
 * minuscules dans le registre.
 */
export function Combo(
  { valeur, onChoix, source, placeholder, ferme, id, requis }:
  {
    valeur: string;
    onChoix: (e: Entree | null) => void;
    source: Entree[];
    placeholder?: string;
    /** Phrase affichée à la place du champ quand l'échelon parent manque. */
    ferme?: string | null;
    id?: string;
    requis?: boolean;
  },
) {
  const [saisie, setSaisie] = useState(valeur);
  const [ouvert, setOuvert] = useState(false);
  const [idx, setIdx] = useState(-1);
  /**
   * ⚠️ UNE COURSE ENTRE LE CLIC ET LA SORTIE DU CHAMP. `onMouseDown` appelle
   * `preventDefault`, ce qui empêche le `blur` dans la plupart des
   * navigateurs — mais pas dans tous, et pas au clavier. Si le `blur` part
   * quand même, sa résolution différée travaillerait sur le texte d'AVANT le
   * clic et commettrait une autre entrée que celle choisie. Ce drapeau dit
   * « un choix vient d'être fait » : le différé passe son tour.
   */
  const choisiJuste = useRef(false);

  useEffect(() => { setSaisie(valeur); }, [valeur]);

  const vus = useMemo(() => {
    const q = saisie.trim().toLocaleLowerCase('fr-FR');
    if (!q || q === valeur.toLocaleLowerCase('fr-FR')) return source;
    return source.filter((o) => o.libelle.toLocaleLowerCase('fr-FR').includes(q)
      || (o.meta ?? '').toLocaleLowerCase('fr-FR').includes(q));
  }, [saisie, source, valeur]);

  if (ferme) return <div className="combo-ferme">{ferme}</div>;

  function choisir(o: Entree) {
    choisiJuste.current = true;
    setSaisie(o.libelle); setOuvert(false); setIdx(-1); onChoix(o);
  }

  return (
    <div className="combo">
      <input
        id={id} type="text" className="combo-saisie" autoComplete="off"
        role="combobox" aria-expanded={ouvert} aria-autocomplete="list"
        required={requis} placeholder={placeholder ?? 'Taper pour chercher…'}
        value={saisie}
        /* ⚠️ LA VALIDITÉ SUIT LA VALEUR COMMISE, PAS LE TEXTE AFFICHÉ. Sans
           cela, un texte tapé qui ne désigne rien passe le `required` du
           navigateur et le formulaire part avec un champ vide. */
        ref={(el) => {
          el?.setCustomValidity(
            saisie.trim() !== '' && valeur === ''
              ? "Choisissez une entrée de la liste : ce texte ne désigne aucune circonscription connue."
              : '');
        }}
        onChange={(e) => {
          choisiJuste.current = false;
          setSaisie(e.target.value); setOuvert(true); setIdx(-1); onChoix(null);
        }}
        onFocus={() => setOuvert(true)}
        onBlur={() => {
          // ⚠️ Différé : un clic sur la liste déclenche `blur` AVANT le clic.
          window.setTimeout(() => {
            setOuvert(false);
            if (choisiJuste.current) { choisiJuste.current = false; return; }
            /* ⚠️ ON COMMET CE QUI DÉSIGNE UNE ENTRÉE, au lieu de se contenter
               de garder le texte à l'écran. C'est ce qui manquait : le champ
               restait affiché plein et vide en valeur, et la cascade en aval
               attendait un parent qu'elle n'aurait jamais. */
            const trouve = resoudreSaisie(saisie, source);
            if (trouve) { setSaisie(trouve.libelle); onChoix(trouve); }
            else if (saisie !== valeur) setSaisie(valeur);
          }, 130);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault(); setOuvert(true);
            setIdx((i) => Math.max(0, Math.min(vus.length - 1, i + (e.key === 'ArrowDown' ? 1 : -1))));
          } else if (e.key === 'Enter' && ouvert && vus[idx]) {
            e.preventDefault(); choisir(vus[idx]!);
          } else if (e.key === 'Escape') { setOuvert(false); }
        }}
      />
      {ouvert && (
        <ul className="combo-liste" role="listbox">
          {vus.length === 0 ? (
            /* ⚠️ ON DIT COMBIEN D'ENTRÉES ONT ÉTÉ FOUILLÉES : « aucun résultat »
               et « la liste est vide » sont deux états opposés. */
            <li className="combo-rien">
              Aucune correspondance parmi les {source.length} entrées du référentiel.
            </li>
          ) : vus.slice(0, 200).map((o, i) => (
            <li key={o.valeur} role="option" aria-selected={i === idx}
                onMouseDown={(e) => { e.preventDefault(); choisir(o); }}>
              {o.echelon && <i className="combo-echelon">{o.echelon}</i>}
              {o.libelle}
              {o.meta && <span className="combo-meta">{o.meta}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ═══ NATIONALITÉ ══════════════════════════════════════════════════════════ */

const ENTREES_NATIONALITE: Entree[] = NATIONALITES.map(([code, libelle]) => ({
  valeur: libelle, libelle, meta: code,
}));

export function ChampNationalite(
  { valeur, onValeur, requis }:
  { valeur: string; onValeur: (v: string) => void; requis?: boolean },
) {
  return (
    <Combo valeur={valeur} requis={requis} source={ENTREES_NATIONALITE}
           placeholder="Guinéenne" onChoix={(e) => onValeur(e?.libelle ?? '')} />
  );
}

/* ═══ CASCADE TERRITORIALE ═════════════════════════════════════════════════
 * ⚠️ UNE SEULE REQUÊTE, PAS UNE PAR FRAPPE. Le référentiel tient en 386 lignes
 * (8 régions, 38 préfectures, 36 communes, 304 sous-préfectures, mesuré le
 * 24/09/2026) : on le charge une fois et on filtre dans la page. Interroger le
 * service à chaque touche ferait cent appels pour une adresse.
 *
 * ⚠️ ET LA CASCADE EST ENTIÈRE. Mesuré : 0 circonscription orpheline sous la
 * région — chaque préfecture a sa région, chaque commune et sous-préfecture sa
 * préfecture. C'est ce qui autorise à fermer le champ enfant tant que le parent
 * n'est pas choisi ; si la cascade était trouée, fermer le champ cacherait des
 * lieux réels.
 */
let promesse: Promise<Circonscription[]> | null = null;
function chargerCirconscriptions(): Promise<Circonscription[]> {
  promesse ??= api.territoire.circonscriptions({ echelon: 'tous' })
    .then((r) => r.circonscriptions)
    .catch((e) => { promesse = null; throw e; });
  return promesse;
}

export interface Adresse {
  region: string; prefecture: string; commune: string; quartier?: string;
}

export function useCirconscriptions() {
  const [tout, setTout] = useState<Circonscription[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  useEffect(() => {
    let vivant = true;
    chargerCirconscriptions()
      .then((c) => { if (vivant) setTout(c); })
      /* ⚠️ ON DIT L'ÉCHEC. Une liste vide et un service injoignable s'affichent
         pareil, et les deux mènent à des gestes opposés : choisir autre chose,
         ou rappeler quelqu'un. */
      .catch(() => { if (vivant) setErreur("Le référentiel territorial n'a pas pu être chargé."); });
    return () => { vivant = false; };
  }, []);
  return { tout, erreur };
}

/** Les trois échelons d'une adresse, en cascade, plus le quartier libre. */
export function ChampsAdresse(
  { adresse, onAdresse, requis, avecQuartier = true }:
  {
    adresse: Adresse;
    onAdresse: (a: Adresse) => void;
    requis?: boolean;
    avecQuartier?: boolean;
  },
) {
  const { tout, erreur } = useCirconscriptions();

  const parEchelon = useCallback((e: Echelon) => (tout ?? []).filter((c) => c.echelon === e), [tout]);
  const regions = useMemo<Entree[]>(
    () => parEchelon('region').map((c) => ({ valeur: String(c.source_id), libelle: c.nom })),
    [parEchelon]);

  const regionChoisie = (tout ?? []).find((c) => c.echelon === 'region' && c.nom === adresse.region);
  const prefectures = useMemo<Entree[]>(() => regionChoisie
    ? parEchelon('prefecture')
        .filter((c) => c.parent_source_id === regionChoisie.source_id)
        .map((c) => ({ valeur: String(c.source_id), libelle: c.nom }))
    : [], [parEchelon, regionChoisie]);

  const prefChoisie = (tout ?? []).find(
    (c) => c.echelon === 'prefecture' && c.nom === adresse.prefecture
      && (!regionChoisie || c.parent_source_id === regionChoisie.source_id));
  const communes = useMemo<Entree[]>(() => prefChoisie
    ? (tout ?? [])
        .filter((c) => (c.echelon === 'commune' || c.echelon === 'sous_prefecture')
          && c.parent_source_id === prefChoisie.source_id)
        .map((c) => ({
          valeur: String(c.source_id), libelle: c.nom,
          /* Le code postal aide à départager deux homonymes — et il n'existe
             qu'à partir de la commune : une case vide plus haut est un FAIT. */
          meta: c.code_postal ?? undefined,
          echelon: c.echelon === 'commune' ? 'com' : 's-p',
        }))
    : [], [tout, prefChoisie]);

  if (erreur) return <p className="erreur" role="alert">{erreur}</p>;
  if (!tout) return <p className="attente">Chargement du référentiel territorial…</p>;

  return (
    <>
      <label className="champ">
        <span className="champ-label">Région{requis && <b className="requis">*</b>}</span>
        <Combo valeur={adresse.region} source={regions} requis={requis}
               onChoix={(e) => onAdresse({ ...adresse, region: e?.libelle ?? '', prefecture: '', commune: '' })} />
      </label>
      <label className="champ">
        <span className="champ-label">Préfecture{requis && <b className="requis">*</b>}</span>
        <Combo valeur={adresse.prefecture} source={prefectures} requis={requis}
               ferme={adresse.region ? null : "Choisissez d'abord la région."}
               onChoix={(e) => onAdresse({ ...adresse, prefecture: e?.libelle ?? '', commune: '' })} />
      </label>
      <label className="champ">
        <span className="champ-label">Commune / Sous-préfecture{requis && <b className="requis">*</b>}</span>
        <Combo valeur={adresse.commune} source={communes} requis={requis}
               ferme={adresse.prefecture ? null : "Choisissez d'abord la préfecture."}
               onChoix={(e) => onAdresse({ ...adresse, commune: e?.libelle ?? '' })} />
      </label>
      {avecQuartier && (
        <label className="champ">
          <span className="champ-label">Quartier / District</span>
          {/* ⚠️ LIBRE, ET C'EST VOULU : le découpage de l'État s'arrête à la
              commune et à la sous-préfecture. Un quartier n'est pas dans
              `ter.circonscription` ; proposer une liste en inventerait une. */}
          <SaisieMajuscule valeur={adresse.quartier ?? ''}
                           onValeur={(v) => onAdresse({ ...adresse, quartier: v })} />
        </label>
      )}
    </>
  );
}

/* ═══ LE NIN ═══════════════════════════════════════════════════════════════
 * ⚠️ LA RÈGLE N'EST PAS RÉÉCRITE ICI : elle vient de `src/partage/nin.ts`, le
 * même module que le service, lui-même d'accord avec `fon.nin_valide` par un
 * essai de vecteurs. Un écran qui recalculerait la clé divergerait au premier
 * correctif appliqué d'un seul côté.
 *
 * ⚠️ POURQUOI VÉRIFIER À L'ÉCRAN ALORS QUE LA BASE REFUSE DÉJÀ. Parce que le
 * refus de la base arrive à l'INSCRIPTION DU TITULAIRE — après le dépôt, en
 * SQL brut (`FON_NIN_INVALIDE`), quand l'usager est reparti. Ici, il est encore
 * devant le comptoir et peut relire sa carte.
 */
/** Les 38 préfectures, pour la lecture du groupe « lieu » du NIN. */
/**
 * Les 38 préfectures, pour la lecture du groupe « lieu » du NIN.
 *
 * ⚠️ REND `null` TANT QUE LE RÉFÉRENTIEL N'EST PAS LÀ. Rendre `[]` ferait dire
 * « ce code ne correspond à aucune préfecture » pendant le chargement, et
 * définitivement si le service est injoignable.
 */
export function usePrefectures(): Array<{ code: string | null; nom: string }> | null {
  const { tout } = useCirconscriptions();
  return useMemo(
    () => (tout === null ? null : tout.filter((c) => c.echelon === 'prefecture')),
    [tout]);
}

export function ChampNin(
  { valeur, onValeur, dateNaissance, onDateDeduite, lieuNaissance, onLieuDeduit, requis = true }:
  {
    valeur: string;
    onValeur: (v: string) => void;
    /** La date saisie au formulaire — confrontée à celle que le numéro porte. */
    dateNaissance?: string;
    onDateDeduite?: (iso: string) => void;
    /** Le lieu saisi au formulaire — confronté à celui que le numéro désigne. */
    lieuNaissance?: string;
    onLieuDeduit?: (nom: string) => void;
    requis?: boolean;
  },
) {
  const net = normaliserNin(valeur);
  const complet = /^\d{15}$/.test(net);
  const verdict = validerNin(net);
  const decode = complet && verdict.valide ? decoderNin(net) : null;
  const prefectures = usePrefectures();
  const lieu = decode ? lieuDuNin(decode.codeLieu, decode.neALEtranger, prefectures) : null;
  const lieuNomme = lieu && 'nom' in lieu ? lieu.nom : null;

  /* ⚠️ LA DATE N'EST REPRISE QUE SI LE CHAMP EST VIDE. Écraser une date saisie
     effacerait le désaccord qu'on veut précisément faire voir. */
  useEffect(() => {
    if (decode?.dateNaissance && !dateNaissance && onDateDeduite) onDateDeduite(decode.dateNaissance);
  }, [decode?.dateNaissance, dateNaissance, onDateDeduite]);

  /* ⚠️ MÊME RÈGLE POUR LE LIEU : on ne remplit que le vide. Écraser une saisie
     effacerait le désaccord qu'on veut justement montrer — et la saisie peut
     être plus fine que la préfecture (une sous-préfecture, un village). */
  useEffect(() => {
    if (lieuNomme && !lieuNaissance && onLieuDeduit) onLieuDeduit(lieuNomme.toLocaleUpperCase('fr-FR'));
  }, [lieuNomme, lieuNaissance, onLieuDeduit]);

  const lignes: Array<[string, string, string]> = [];
  if (!net) {
    lignes.push(['neutre', 'Le numéro est obligatoire.',
      "Il relie cette personne à toutes les applications foncières et fiscales de l'État."]);
  } else if (!/^\d+$/.test(net)) {
    lignes.push(['ko', 'Caractères non numériques.',
      'Le NIN ne contient que des chiffres ; espaces, tirets et points sont ignorés.']);
  } else if (!complet) {
    lignes.push(['att', `${net.length} chiffre(s) sur 15.`, `Il en manque ${15 - net.length}.`]);
  } else if (!verdict.valide) {
    lignes.push(['ko', 'Clé de contrôle fausse.',
      "Le registre refusera l'inscription (FON_NIN_INVALIDE) — ici, l'usager est encore devant vous."]);
  } else {
    lignes.push(['ok', 'Clé de contrôle juste.',
      "Cohérence du numéro, pas existence de la personne : un modulo 10 laisse passer environ un numéro inventé sur dix."]);
    lignes.push([decode?.sexe ? 'ok' : 'att',
      decode?.sexe ? `Sexe déclaré : ${decode.sexe}.` : 'Le premier chiffre sort de la légende connue.',
      decode?.neALEtranger ? "Né(e) hors de Guinée — ce qui ne dit RIEN de la nationalité." : '']);
    if (decode?.siecleAmbigu) {
      lignes.push(['att', "Le siècle n'est pas tranché.",
        `L'année « ${net.slice(1, 3)} » se lit 19xx ou 20xx et les deux restent plausibles : le numéro ne tranche pas. La date saisie fait foi.`]);
    } else if (!decode?.dateNaissance) {
      lignes.push(['att', 'Le numéro ne porte pas de date lisible.', 'Quantième impossible ou hors plage.']);
    } else if (dateNaissance && dateNaissance !== decode.dateNaissance) {
      lignes.push(['ko', 'Le numéro et la date saisie se contredisent.',
        `Le NIN dit ${decode.dateNaissance}, le formulaire dit ${dateNaissance}. L'un des deux est faux — et c'est cette personne que le registre reliera à un patrimoine.`]);
    } else if (dateNaissance) {
      lignes.push(['ok', 'Le numéro et la date saisie concordent.', '']);
    }

    /* ── Le groupe « lieu », et ce qu'il vaut ───────────────────────────── */
    if (lieu && 'raison' in lieu) {
      lignes.push(['att', `Groupe « lieu » : ${decode?.codeLieu}.`, lieu.raison]);
    } else if (lieuNomme && lieuNaissance
               && aplatir(lieuNaissance) !== aplatir(lieuNomme)) {
      /* ⚠️ « ATT » ET NON « KO » : la table code → préfecture n'est établie
         que par un cas, et la personne peut être née dans une sous-préfecture
         ou un village de cette préfecture — deux noms, un seul lieu. C'est à
         l'agent de trancher, avec les deux valeurs sous les yeux. */
      lignes.push(['att', `Le numéro désigne ${lieuNomme}, le formulaire dit ${lieuNaissance}.`,
        "Le groupe « lieu » du NIN porte la préfecture de naissance. Un village ou une "
        + 'sous-préfecture de cette même préfecture est un accord, pas un écart — vérifiez.']);
    } else if (lieuNomme && lieuNaissance) {
      lignes.push(['ok', `Le numéro et le lieu saisi concordent — ${lieuNomme}.`, '']);
    } else if (lieuNomme) {
      lignes.push(['ok', `Le numéro désigne ${lieuNomme}.`,
        'Reporté dans « Lieu de naissance », qui reste modifiable.']);
    }
  }

  const segments: Array<[string, string]> = complet ? [
    [net.slice(0, 1), 'sexe'], [net.slice(1, 3), 'année'], [net.slice(3, 5), 'mois'],
    [net.slice(5, 7), 'jour'], [net.slice(7, 10), 'lieu'], [net.slice(10, 13), 'extrait'],
    [net.slice(13, 14), 'code'], [net.slice(14, 15), 'clé'],
  ] : [];

  return (
    <div className="nin-bloc">
      <input type="text" className="combo-saisie nin-saisie" inputMode="numeric"
             placeholder="15 chiffres" maxLength={19} required={requis} value={valeur}
             onChange={(e) => onValeur(e.target.value.replace(/[^\d\s\-.]/g, ''))} />
      <div className="nin-verdict">
        {segments.length > 0 && (
          <div className="nin-decompose">
            {segments.map(([v, n], i) => (
              <div key={n} className={`nin-seg${i === 7 ? ` nin-cle${verdict.valide ? '' : ' nin-faux'}` : ''}`}>
                <b>{v}</b><small>{n}</small>
              </div>
            ))}
          </div>
        )}
        {lignes.map(([genre, titre, detail]) => (
          <div key={titre} className={`nin-ligne ${genre}`}>
            <b>{titre}</b>{detail && <span>{detail}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══ LE LIEN D'UN TÉMOIN AVEC LES PARTIES ═════════════════════════════════
 * ⚠️ LISTE FERMÉE + ÉCHAPPATOIRE, et l'échappatoire n'est pas une facilité :
 * ce champ sert à DÉCLARER un conflit d'intérêt. Une liste sans « autre »
 * ferait choisir le voisin le moins faux, et le champ cesserait de dire la
 * vérité qu'on lui demande. Il était libre jusqu'au 24/09/2026 — donc vide
 * neuf fois sur dix, ce qui est la même chose que taire le lien.
 *
 * ⚠️ LA VALEUR STOCKÉE RESTE UNE PHRASE. `temoinSchema.lienAvecLesParties` est
 * un `z.string().max(200)` que l'acte imprime tel quel : envoyer un code
 * obligerait à traduire à l'impression, et une traduction manquante
 * imprimerait « parent_donateur » sur un acte d'État.
 */
export const LIENS_TEMOIN: readonly string[] = [
  'Aucun lien',
  'Parent du cédant',
  'Parent du bénéficiaire',
  'Allié par alliance (belle-famille)',
  'Voisin du fonds',
  'Employeur ou employé d\'une partie',
  'Associé en affaires d\'une partie',
  'Ami ou connaissance',
];
export const LIEN_AUTRE = 'Autre — à préciser';

/* ═══ LE PORTRAIT D'UNE PERSONNE À L'ACTE ══════════════════════════════════
 * Demande du fondateur, 24/09/2026 : « il faut un espace pour la photo du
 * donataire, du donateur et des témoins ».
 *
 * ⚠️ LA RÈGLE EXISTE DÉJÀ POUR UN AUTRE ACTE. `fon.garde_attestation`
 * (migration 068) REFUSE de délivrer une attestation de cession sans la photo
 * du cédant ET celle de l'acquéreur. Mesuré le 24/09/2026 : 10 attestations,
 * 9 portant les deux. La donation n'en demandait aucune, et AUCUNE table du
 * registre ne portait de photo de TÉMOIN.
 *
 * ⚠️⚠️ LA VIDÉO EST MONTÉE AVANT D'ÊTRE ALIMENTÉE. Poser le flux sur une
 * référence pas encore dans le DOM allume la caméra et laisse un cadre NOIR,
 * sans une erreur, sur tout appareil. Ce dépôt l'a déjà payé sur un scanner.
 *
 * ⚠️ L'IMAGE EST RÉDUITE AVANT D'ÊTRE GARDÉE. Quatre portraits en pleine
 * définition dépassent le quota du navigateur, et le brouillon disparaît en
 * silence au moment où l'agent croit l'avoir enregistré.
 */
export function ChampPortrait(
  { valeur, onValeur, qui, obligatoire }:
  { valeur: string; onValeur: (v: string) => void; qui: string; obligatoire?: boolean },
) {
  const cadre = useRef<HTMLDivElement>(null);
  const fichier = useRef<HTMLInputElement>(null);
  const [flux, setFlux] = useState<MediaStream | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  /** Vrai dès que la caméra a livré sa première image — avant, rien à prendre. */
  const [pret, setPret] = useState(false);
  const video = useRef<HTMLVideoElement>(null);

  useEffect(() => () => { flux?.getTracks().forEach((t) => t.stop()); }, [flux]);

  /**
   * ⚠️⚠️ LE FLUX S'ATTACHE DANS UN EFFET, PAS DANS UN `setTimeout(0)`.
   *
   * Mesuré en production le 24/09/2026 : l'aperçu restait VIDE et « Déclencher »
   * répondait « la caméra n'a livré aucune image ». La caméra s'ouvrait
   * pourtant — le bouton avait changé. Cause : `setFlux` déclenche un rendu, et
   * `video.current` n'existe qu'APRÈS que React a posé l'élément. Un
   * `setTimeout(0)` PARIE sur cet ordre ; il n'en garantit rien, et le pari
   * perd dès que le rendu est différé. Un effet, lui, ne court qu'une fois le
   * DOM posé — c'est la garantie même de React.
   *
   * ⚠️ `muted` EST POSÉ À LA MAIN. React ne reflète pas fidèlement cet
   * attribut sur `<video>` ; sans lui, la lecture automatique est REFUSÉE par
   * le navigateur, et un flux attaché mais jamais lu donne exactement le même
   * écran vide.
   *
   * ⚠️ ET L'ON ATTEND LES MÉTADONNÉES. `videoWidth` vaut 0 tant qu'elles ne
   * sont pas arrivées : déclencher dans cet intervalle produisait le message
   * d'erreur sur une caméra parfaitement saine. « Déclencher » reste donc
   * DÉSACTIVÉ jusqu'à la première image — un bouton qui ne peut pas réussir ne
   * doit pas être cliquable.
   */
  useEffect(() => {
    const v = video.current;
    if (!flux || !v) return;
    v.srcObject = flux;
    v.muted = true;
    v.play().catch(() => setErreur(
      "Le navigateur a refusé de démarrer l'aperçu. Utilisez « Téléverser »."));
  }, [flux]);

  async function ouvrirCamera() {
    setErreur(null); setPret(false);
    /* ⚠️ `mediaDevices` N'EXISTE PAS HORS CONTEXTE SÉCURISÉ. En HTTP, l'objet
       est absent et l'appel lève `TypeError` — un message sur le « refus » de
       la caméra enverrait chercher un réglage de navigateur au lieu du schéma
       de l'adresse. */
    if (!navigator.mediaDevices?.getUserMedia) {
      setErreur("Ce navigateur n'expose pas la caméra (page servie hors HTTPS, "
        + 'ou navigateur trop ancien). Utilisez « Téléverser ».');
      return;
    }
    try {
      const f = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: { ideal: 720 } } });
      setFlux(f);
    } catch (e) {
      /* ⚠️ ON DIT POURQUOI, ET LE NOM DE L'ERREUR SÉPARE DES GESTES OPPOSÉS :
         `NotAllowedError` se répare dans le navigateur, `NotFoundError` veut
         dire qu'il n'y a pas de caméra sur ce poste. */
      const nom = (e as Error)?.name ?? 'refus';
      setErreur(nom === 'NotFoundError' || nom === 'OverconstrainedError'
        ? "Aucune caméra n'est disponible sur ce poste. Utilisez « Téléverser »."
        : nom === 'NotAllowedError'
          ? 'La caméra a été refusée pour ce site. Autorisez-la dans le navigateur, '
            + 'ou utilisez « Téléverser ».'
          : `La caméra n'a pas pu être ouverte (${nom}). Utilisez « Téléverser ».`);
    }
  }
  function declencher() {
    const v = video.current;
    if (!v?.videoWidth) { setErreur("La caméra n'a livré aucune image : rien n'a été pris."); return; }
    onValeur(reduireEnJpeg(v));
    flux?.getTracks().forEach((t) => t.stop()); setFlux(null); setPret(false);
  }

  return (
    <fieldset className="sous-cadre portrait-bloc">
      <legend>Photographie — {qui}{obligatoire ? ' (obligatoire)' : ''}</legend>
      <div className="portrait-corps">
        <div className="portrait-cadre" ref={cadre}>
          {flux ? <video ref={video} autoPlay playsInline muted
                         onLoadedMetadata={() => setPret(true)} />
            : valeur ? <img src={valeur} alt={`Portrait — ${qui}`} />
            : <span className="portrait-vide">Aucune photographie</span>}
        </div>
        <div className="portrait-actions">
          <div className="portrait-rangs">
            {flux ? (
              <>
                <button type="button" className="bouton" onClick={declencher} disabled={!pret}
                        title={pret ? 'Prendre la photographie' : "L'aperçu n'a pas encore démarré"}>
                  {pret ? 'Déclencher' : 'Démarrage de l’aperçu…'}
                </button>
                <button type="button" className="bouton discret"
                        onClick={() => { flux.getTracks().forEach((t) => t.stop()); setFlux(null); setPret(false); }}>Annuler</button>
              </>
            ) : (
              <>
                <button type="button" className="bouton discret" onClick={() => void ouvrirCamera()}>
                  Prendre la photo
                </button>
                <button type="button" className="bouton discret" onClick={() => fichier.current?.click()}>
                  Téléverser
                </button>
                {valeur && (
                  <button type="button" className="bouton discret" onClick={() => onValeur('')}>Retirer</button>
                )}
              </>
            )}
            <input ref={fichier} type="file" accept="image/*" hidden
                   onChange={(e) => {
                     const f = e.target.files?.[0]; if (!f) return;
                     const l = new FileReader();
                     l.onload = () => {
                       const i = new Image();
                       i.onload = () => onValeur(reduireEnJpeg(i));
                       i.onerror = () => setErreur("Ce fichier n'est pas une image lisible.");
                       i.src = String(l.result);
                     };
                     l.readAsDataURL(f);
                   }} />
          </div>
          {erreur && <p className="portrait-erreur" role="alert">{erreur}</p>}
          <p className="aide">
            {obligatoire
              ? "Sans photographie, l'acte ne se délivre pas : la règle existe déjà au registre pour l'attestation de cession, dont la délivrance est refusée quand la photo d'une partie manque."
              : "Un témoin atteste une identité : la sienne doit être établie comme celle des parties."}
          </p>
        </div>
      </div>
    </fieldset>
  );
}
